import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarIcon, PencilIcon, ImageIcon, ChevronDownIcon, ChevronUpIcon, Loader2, XIcon, ImageOff } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { MonoValue } from "@/components/redesign/MonoValue";
import { projectsAPI } from "@/api/projects";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import useSubscription from "@/contexts/SubscriptionContext";
import { canUploadImages } from "@/lib/featureGates";
import { useNavigate } from "react-router-dom";
import { constructionStagesAPI } from "@/api/construction-stages";
import { v4 as uuidv4 } from 'uuid';
import NoteDialog from "./NoteDialog";
import UniversalPhotoUploadDialog from "@/components/common/UniversalPhotoUploadDialog";
import StageDeleteConfirmationDialog from "./DeleteConfirmationDialog";
import { uploadFile, deleteFile } from "@/lib/storage-adapter";
import ImagePreviewDialog from "./ImagePreviewDialog";
import SecureImage from "@/components/SecureImage";

interface Stage {
  id: string;
  name: string;
  completed: boolean;
  completedDate?: Date;
  phase: string;
  phaseColor: string;
  note?: string;
  photo?: string;
  orderIndex?: number;
}

interface ProgressTabProps {
  projectId: string;
  projectStatus?: "active" | "on-track" | "delayed" | "completed";
}

const ProgressTab: React.FC<ProgressTabProps> = ({ projectId, projectStatus = "active" }) => {
  const { t } = useTranslation();
  
  const [openPhases, setOpenPhases] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { status: subscriptionStatus } = useSubscription();
  const tierBlocked = !canUploadImages(subscriptionStatus);

  // State for dialogs
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [deleteType, setDeleteType] = useState<'note' | 'photo'>('note');
  const [isDeleting, setIsDeleting] = useState(false);

  // Add state for image preview dialog
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState("");

  const isProjectCompleted = projectStatus === "completed";

  // Predefined construction stages - this is the standard template
  const [stages, setStages] = useState<Stage[]>([]);
  const [originalStages, setOriginalStages] = useState<Stage[]>([]);

  // Initialize default stages with translation keys (not translated text)
  const getDefaultStages = (): Stage[] => [
    // 🟠 Preparation Phase
    {
      id: uuidv4(),
      name: 'progress_tab.stages.site_setup',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.excavation',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.soil_study',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_digging',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.concrete_pouring',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.waterproofing',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.rebar_placement',
      completed: false,
      phase: 'progress_tab.phases.preparation',
      phaseColor: "bg-orange-500"
    },
    
    // 🔵 Foundation Phase
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_formwork',
      completed: false,
      phase: 'progress_tab.phases.foundation',
      phaseColor: "bg-blue-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_concrete',
      completed: false,
      phase: 'progress_tab.phases.foundation',
      phaseColor: "bg-blue-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_curing',
      completed: false,
      phase: 'progress_tab.phases.foundation',
      phaseColor: "bg-blue-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_inspection',
      completed: false,
      phase: 'progress_tab.phases.foundation',
      phaseColor: "bg-blue-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.foundation_insulation',
      completed: false,
      phase: 'progress_tab.phases.foundation',
      phaseColor: "bg-blue-500"
    },
    
    // 🟣 Building Structure
    {
      id: uuidv4(),
      name: 'progress_tab.stages.basement_columns',
      completed: false,
      phase: 'progress_tab.phases.structure',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.ground_floor',
      completed: false,
      phase: 'progress_tab.phases.structure',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.upper_floors',
      completed: false,
      phase: 'progress_tab.phases.structure',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.stair_formwork',
      completed: false,
      phase: 'progress_tab.phases.structure',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.rough_construction',
      completed: false,
      phase: 'progress_tab.phases.structure',
      phaseColor: "bg-purple-500"
    },
    
    // 🟢 Walls and Roof
    {
      id: uuidv4(),
      name: 'progress_tab.stages.brick_walls',
      completed: false,
      phase: 'progress_tab.phases.walls_roof',
      phaseColor: "bg-green-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.slab_insulation',
      completed: false,
      phase: 'progress_tab.phases.walls_roof',
      phaseColor: "bg-green-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.roof_truss',
      completed: false,
      phase: 'progress_tab.phases.walls_roof',
      phaseColor: "bg-green-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.roof_covering',
      completed: false,
      phase: 'progress_tab.phases.walls_roof',
      phaseColor: "bg-green-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.gutters',
      completed: false,
      phase: 'progress_tab.phases.walls_roof',
      phaseColor: "bg-green-500"
    },
    
    // 🔴 Installation Works
    {
      id: uuidv4(),
      name: 'progress_tab.stages.electrical_installation',
      completed: false,
      phase: 'progress_tab.phases.installations',
      phaseColor: "bg-red-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.plumbing_installation',
      completed: false,
      phase: 'progress_tab.phases.installations',
      phaseColor: "bg-red-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.gas_installation',
      completed: false,
      phase: 'progress_tab.phases.installations',
      phaseColor: "bg-red-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.infrastructure_connections',
      completed: false,
      phase: 'progress_tab.phases.installations',
      phaseColor: "bg-red-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.ventilation_ducts',
      completed: false,
      phase: 'progress_tab.phases.installations',
      phaseColor: "bg-red-500"
    },
    
    // 🟡 Interior Works
    {
      id: uuidv4(),
      name: 'progress_tab.stages.interior_plaster',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.exterior_plaster',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.thermal_insulation',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.paint_primer',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.ceramic_tiles',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.parquet_flooring',
      completed: false,
      phase: 'progress_tab.phases.interior',
      phaseColor: "bg-yellow-500"
    },
    
    // 🔵 Final Systems and Finishing
    {
      id: uuidv4(),
      name: 'progress_tab.stages.interior_doors',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.window_installation',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.electrical_fixtures',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.plumbing_fixtures',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.final_painting',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.balcony_terrace',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.kitchen_cabinets',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.hvac_installation',
      completed: false,
      phase: 'progress_tab.phases.finishing',
      phaseColor: "bg-cyan-500"
    },
    
    // 🟣 Completion and Delivery
    {
      id: uuidv4(),
      name: 'progress_tab.stages.final_cleaning',
      completed: false,
      phase: 'progress_tab.phases.completion',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.owner_inspection',
      completed: false,
      phase: 'progress_tab.phases.completion',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.deficiency_repairs',
      completed: false,
      phase: 'progress_tab.phases.completion',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.ready_for_handover',
      completed: false,
      phase: 'progress_tab.phases.completion',
      phaseColor: "bg-purple-500"
    },
    {
      id: uuidv4(),
      name: 'progress_tab.stages.handover',
      completed: false,
      phase: 'progress_tab.phases.completion',
      phaseColor: "bg-purple-500"
    }
  ];

  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});

  // Initialize openPhases state after translations are available
  useEffect(() => {
    if (stages.length > 0) {
      // Extract unique phases from the current stages and set them all to open
      const uniquePhases = [...new Set(stages.map(stage => stage.phase))];
      const newOpenPhases: Record<string, boolean> = {};
      uniquePhases.forEach(phase => {
        newOpenPhases[phase] = true;
      });
      setOpenPhases(newOpenPhases);
    } else {
      // Fallback to default phases if no stages are loaded yet
      setOpenPhases({
        [t('progress_tab.phases.preparation')]: true,
        [t('progress_tab.phases.foundation')]: true,
        [t('progress_tab.phases.structure')]: true,
        [t('progress_tab.phases.walls_roof')]: true,
        [t('progress_tab.phases.installations')]: true,
        [t('progress_tab.phases.interior')]: true,
        [t('progress_tab.phases.finishing')]: true,
        [t('progress_tab.phases.completion')]: true,
      });
    }
  }, [t, stages]);

  // Create reverse mapping from Turkish text to translation keys for existing projects
  const createReverseMapping = () => {
    // Phase mappings (Turkish -> translation key)
    const phaseMapping: Record<string, string> = {
      "Hazırlık Aşaması": "progress_tab.phases.preparation",
      "Temel Aşaması": "progress_tab.phases.foundation",
      "Yapı İskeleti": "progress_tab.phases.structure",
      "Duvarlar ve Çatı": "progress_tab.phases.walls_roof",
      "Tesisat İşleri": "progress_tab.phases.installations",
      "İç Mekan İşleri": "progress_tab.phases.interior",
      "Son Sistemler ve Bitirme": "progress_tab.phases.finishing",
      "Tamamlama ve Teslim": "progress_tab.phases.completion"
    };

    // Stage mappings (Turkish -> translation key)
    const stageMapping: Record<string, string> = {
      "Şantiye kurulumu": "progress_tab.stages.site_setup",
      "Hafriyat": "progress_tab.stages.excavation",
      "Zemin etüdü": "progress_tab.stages.soil_study",
      "Temel kazısı": "progress_tab.stages.foundation_digging",
      "Grobeton dökme": "progress_tab.stages.concrete_pouring",
      "Su yalıtımı uygulaması": "progress_tab.stages.waterproofing",
      "Demir donatı yerleştirme": "progress_tab.stages.rebar_placement",
      "Temel kalıpları hazırlanması": "progress_tab.stages.foundation_formwork",
      "Temel betonu dökme": "progress_tab.stages.foundation_concrete",
      "Temel kürleme": "progress_tab.stages.foundation_curing",
      "Radye temel kontrolü": "progress_tab.stages.foundation_inspection",
      "Temel yalıtımı ve dolgusu": "progress_tab.stages.foundation_insulation",
      "Bodrum kat kolon ve perde": "progress_tab.stages.basement_columns",
      "Zemin kat kolon ve döşeme": "progress_tab.stages.ground_floor",
      "Üst katların inşası": "progress_tab.stages.upper_floors",
      "Merdiven kalıpları ve dökümü": "progress_tab.stages.stair_formwork",
      "Kaba inşaat tamamlandı": "progress_tab.stages.rough_construction",
      "Tuğla duvar örme": "progress_tab.stages.brick_walls",
      "Asmolen / döşeme yalıtımı": "progress_tab.stages.slab_insulation",
      "Çatı makası montajı": "progress_tab.stages.roof_truss",
      "Çatı kaplama": "progress_tab.stages.roof_covering",
      "Yağmur oluğu ve iniş borusu": "progress_tab.stages.gutters",
      "Elektrik tesisatı döşeme": "progress_tab.stages.electrical_installation",
      "Sıhhi tesisat döşeme": "progress_tab.stages.plumbing_installation",
      "Doğalgaz tesisatı": "progress_tab.stages.gas_installation",
      "Altyapı bağlantıları": "progress_tab.stages.infrastructure_connections",
      "Havalandırma kanalları": "progress_tab.stages.ventilation_ducts",
      "İç sıva": "progress_tab.stages.interior_plaster",
      "Dış cephe sıvası": "progress_tab.stages.exterior_plaster",
      "Isı yalıtım kaplama": "progress_tab.stages.thermal_insulation",
      "Astar boya": "progress_tab.stages.paint_primer",
      "Seramik döşeme": "progress_tab.stages.ceramic_tiles",
      "Parke döşeme": "progress_tab.stages.parquet_flooring",
      "İç kapı montajı": "progress_tab.stages.interior_doors",
      "Pencere montajı": "progress_tab.stages.window_installation",
      "Elektrik armatürleri": "progress_tab.stages.electrical_fixtures",
      "Sıhhi tesisat armatürleri": "progress_tab.stages.plumbing_fixtures",
      "Son kat boya": "progress_tab.stages.final_painting",
      "Balkon ve teras": "progress_tab.stages.balcony_terrace",
      "Mutfak dolapları": "progress_tab.stages.kitchen_cabinets",
      "Isıtma soğutma sistemi": "progress_tab.stages.hvac_installation",
      "Son temizlik": "progress_tab.stages.final_cleaning",
      "Mal sahibi kontrolleri": "progress_tab.stages.owner_inspection",
      "Eksik ve ayıp giderme": "progress_tab.stages.deficiency_repairs",
      "Teslime hazır": "progress_tab.stages.ready_for_handover",
      "Teslim": "progress_tab.stages.handover"
    };

    return { phaseMapping, stageMapping };
  };

  // Re-translate stages when language changes
  useEffect(() => {
    if (originalStages.length > 0) {
      const { phaseMapping, stageMapping } = createReverseMapping();
      
      const translatedStages = originalStages.map(stage => {
        let translatedName = stage.name;
        let translatedPhase = stage.phase;

        // If it's a translation key, translate it
        if (stage.name.startsWith('progress_tab.')) {
          translatedName = t(stage.name);
        } 
        // If it's Turkish text, convert to translation key first, then translate
        else if (stageMapping[stage.name]) {
          translatedName = t(stageMapping[stage.name]);
        }

        // Same for phase
        if (stage.phase.startsWith('progress_tab.')) {
          translatedPhase = t(stage.phase);
        }
        else if (phaseMapping[stage.phase]) {
          translatedPhase = t(phaseMapping[stage.phase]);
        }

        return {
          ...stage,
          name: translatedName,
          phase: translatedPhase
        };
      });
      setStages(translatedStages);
    }
  }, [t, originalStages]);

  // Fetch project progress when component mounts
  useEffect(() => {
    const fetchProjectProgress = async () => {
      if (!projectId) return;
      
      setIsLoading(true);
      
      try {
        // Get construction stages for the project from the API
        const { data, error } = await constructionStagesAPI.getProjectStages(projectId);
        
        if (error) {
          
          throw error;
        }
        
        if (data && data.length > 0) {
          // Use the stages from the database and translate them if they contain translation keys
          // The API returns `photoUrl`, but Stage (and the rest of this component) reads `photo`
          const normalizedStages = data.map(stage => ({ ...stage, photo: stage.photoUrl }));
          setOriginalStages(normalizedStages);
          const translatedStages = normalizedStages.map(stage => ({
            ...stage,
            name: stage.name.startsWith('progress_tab.') ? t(stage.name) : stage.name,
            phase: stage.phase.startsWith('progress_tab.') ? t(stage.phase) : stage.phase
          }));
          setStages(translatedStages);
        } else {
          
          // If no stages exist for this project, create the default stages
          const { data: createResult, error: createError } = await constructionStagesAPI.createDefaultStages(projectId);
          
          if (createError) {
            
            throw createError;
          }
          
          
          // Then fetch the newly created stages
          const { data: newStages, error: newError } = await constructionStagesAPI.getProjectStages(projectId);
          
          if (newError) {
            
            throw newError;
          }
          
          if (newStages && newStages.length > 0) {

            const normalizedNewStages = newStages.map(stage => ({ ...stage, photo: stage.photoUrl }));
            setOriginalStages(normalizedNewStages);
            const translatedNewStages = normalizedNewStages.map(stage => ({
              ...stage,
              name: stage.name.startsWith('progress_tab.') ? t(stage.name) : stage.name,
              phase: stage.phase.startsWith('progress_tab.') ? t(stage.phase) : stage.phase
            }));
            setStages(translatedNewStages);
          } else {
            
            // Use the default stages from the component state if API fails
            const defaultStages = getDefaultStages();
            setOriginalStages(defaultStages);
            setStages(defaultStages);
            toast.warning(t('progress_tab.default_stages_warning'));
          }
        }
      } catch (err) {
        
        toast.error(t('progress_tab.progress_load_error'));
        // We'll still use the default stages if API fails completely
        const defaultStages = getDefaultStages();
        setOriginalStages(defaultStages);
        setStages(defaultStages);
        
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjectProgress();
  }, [projectId, t]);

  // Group stages by phase
  const phaseGroups: Record<string, Stage[]> = stages.reduce((groups, stage) => {
    // Use the current phase name as key for grouping
    const phaseKey = stage.phase;
    if (!groups[phaseKey]) {
      groups[phaseKey] = [];
    }
    groups[phaseKey].push(stage);
    return groups;
  }, {} as Record<string, Stage[]>);

  // Calculate overall progress
  const completedCount = stages.filter(stage => stage.completed).length;
  const totalCount = stages.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  // Toggle the collapse state of a phase group
  const togglePhaseCollapse = (phase: string) => {
    setOpenPhases(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }));
  };

  // Extract the file path from a Supabase URL
  const getFilePathFromUrl = (url: string): string | null => {
    try {
      const storageUrl = new URL(url);
      const pathParts = storageUrl.pathname.split('/');
      // Remove /storage/v1/object/public/ from the path
      const bucketIndex = pathParts.findIndex(part => part === 'public') + 1;
      if (bucketIndex > 0 && bucketIndex < pathParts.length) {
        return pathParts.slice(bucketIndex).join('/');
      }
      return null;
    } catch (error) {
      
      return null;
    }
  };

  // Add a note to a stage
  const handleAddNote = (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;
    
    setSelectedStage(stage);
    setNoteDialogOpen(true);
  };

  // Save note from dialog
  const handleSaveNote = async (note: string) => {
    if (!selectedStage) return;
    
    try {
      const { error } = await constructionStagesAPI.updateStageNote(selectedStage.id, note);
      if (error) throw error;
      
      // Update local state
      setOriginalStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, note } : s
        )
      );
      setStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, note } : s
        )
      );
      
      toast.success(t('progress_tab.note_saved'));
    } catch (err) {
      
      toast.error(t('progress_tab.note_add_error'));
      throw err;
    }
  };
  
  // Add a photo to a stage
  const handleAddPhoto = (stageId: string) => {
    if (tierBlocked) {
      toast.info(t('photo_upload.premium_required'));
      navigate('/subscription');
      return;
    }
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;

    setSelectedStage(stage);
    setPhotoDialogOpen(true);
  };

  // Save photo URL from dialog
  const handlePhotoUploaded = async (photoUrl: string) => {
    if (!selectedStage) return;
    
    try {
      const { error } = await constructionStagesAPI.updateStagePhoto(selectedStage.id, photoUrl);
      if (error) throw error;
      
      // Update local state
      setOriginalStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, photo: photoUrl } : s
        )
      );
      setStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, photo: photoUrl } : s
        )
      );
      
      toast.success(t('progress_tab.photo_uploaded'));
    } catch (err) {
      
      toast.error(t('progress_tab.photo_add_error'));
    }
  };

  // Handle checkbox toggle
  const handleToggleStage = async (stageId: string) => {
    const stage = stages.find(s => s.id === stageId);
    if (!stage) return;
    
    // If stage is currently completed and has attachments, show confirmation dialog
    if (stage.completed && (stage.note || stage.photo)) {
      setSelectedStage(stage);
      setDeleteDialogOpen(true);
      return;
    }
    
    // Otherwise, proceed with the toggle
    await toggleStageCompletion(stageId);
  };

  // Delete note and photo if needed, then toggle stage completion
  const handleConfirmDelete = async () => {
    if (!selectedStage) return;
    
    setIsDeleting(true);
    try {
      // Delete the photo from storage if it exists
      if (selectedStage.photo) {
        const filePath = getFilePathFromUrl(selectedStage.photo);
        if (filePath) {
          await deleteFile(filePath, 'site-photos');
        }
      }
      
      // Clear both note and photo in the database
      await constructionStagesAPI.updateStage(selectedStage.id, {
        note: '',
        photoUrl: ''
      });
      
      // Update local state
      setOriginalStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, note: undefined, photo: undefined } : s
        )
      );
      setStages(prevStages => 
        prevStages.map(s => 
          s.id === selectedStage.id ? { ...s, note: undefined, photo: undefined } : s
        )
      );
      
      // Now toggle the stage completion
      await toggleStageCompletion(selectedStage.id);
      
      toast.success(t('progress_tab.stage_attachments_deleted'));
    } catch (err) {
      
      toast.error(t('progress_tab.stage_attachments_delete_error'));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Toggle stage completion without dealing with attachments
  const toggleStageCompletion = async (stageId: string) => {
    setIsSaving(true);
    
    try {
      const stage = stages.find(s => s.id === stageId);
      if (!stage) throw new Error("Stage not found");
      
      // Update in database
      const { data, error } = await constructionStagesAPI.updateStage(stageId, {
        completed: !stage.completed,
        completedDate: !stage.completed ? new Date() : undefined
      });
      
      if (error) throw error;
      
      // Update the local state
      setOriginalStages(prevStages => 
        prevStages.map(s => 
          s.id === stageId 
            ? { 
                ...s, 
                completed: !s.completed,
                completedDate: !s.completed ? new Date() : undefined 
              } 
            : s
        )
      );
      setStages(prevStages => 
        prevStages.map(s => 
          s.id === stageId 
            ? { 
                ...s, 
                completed: !s.completed,
                completedDate: !s.completed ? new Date() : undefined 
              } 
            : s
        )
      );
      
      // Update the project progress automatically
      const newStages = stages.map(s => 
        s.id === stageId 
          ? { ...s, completed: !s.completed } 
          : s
      );
      const completedCount = newStages.filter(s => s.completed).length;
      const totalCount = newStages.length;
      const progress = Math.round((completedCount / totalCount) * 100);
      
      try {
        await projectsAPI.updateProject(projectId, {
          progress: progress
        });
      } catch (progressError) {
        
        toast.warning(t('progress_tab.stage_updated_progress_failed'));
        return;
      }
      
      toast.success(data.completed ? t('progress_tab.stage_completed') : t('progress_tab.stage_not_completed'));
      
    } catch (err) {
      
      toast.error(t('progress_tab.stage_update_error'));
    } finally {
      setIsSaving(false);
    }
  };

  // Function to handle image load error
  const handleImageError = (imageUrl: string) => {
    if (!failedImages[imageUrl]) {
      setFailedImages(prev => ({
        ...prev,
        [imageUrl]: true
      }));
      
    }
  };

  // Add function to open image preview
  const handleImageClick = (e: React.MouseEvent, stage: Stage) => {
    e.preventDefault();
    if (stage.photo && !failedImages[stage.photo]) {
      setSelectedStage(stage);
      setPreviewImageUrl(stage.photo);
      setImagePreviewOpen(true);
    }
  };

  return (
    <div className="progress-tab pt-2">
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          <span className="ml-2 text-gray-500">{t('progress_tab.loading')}</span>
        </div>
      ) : (
        <>
          <div className="mb-4">
            {/* Progress summary card */}
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[15px] font-semibold text-gray-900">{t('progress_tab.title')}</h3>
                <MonoValue value={progressPercentage} suffix="%" tone="brand" size="md" />
              </div>
              <div className="w-full h-2 rounded-full bg-gray-100 mb-1">
                <div
                  className="h-2 rounded-full bg-brand-600 transition-all"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="text-[12px] text-gray-500 mt-1">
                {isProjectCompleted
                  ? t('progress_tab.project_completed')
                  : t('progress_tab.stages_completed', { completed: completedCount, total: totalCount })}
              </p>
            </div>

            {totalCount === 0 ? (
              <div className="text-center p-4 bg-warning-50 text-warning-600 rounded-xl border border-warning-300">
                <p className="mb-2 font-semibold">{t('progress_tab.stages_failed')}</p>
                <p className="text-sm">{t('progress_tab.no_stages_message')}</p>
              </div>
            ) : (
              Object.entries(phaseGroups).map(([phase, phaseStages]) => (
                <Collapsible
                  key={phase}
                  open={openPhases[phase]}
                  onOpenChange={() => togglePhaseCollapse(phase)}
                  className="mb-3"
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex justify-between items-center py-2.5 px-4 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-left"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`h-3 w-3 rounded-full ${phaseStages[0].phaseColor}`} />
                        <span className="text-[14px] font-semibold text-gray-900">{phase}</span>
                        <span className="text-[11px] text-gray-500 font-mono">
                          {phaseStages.filter(s => s.completed).length}/{phaseStages.length}
                        </span>
                      </div>
                      {openPhases[phase] ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="bg-white border border-t-0 border-gray-200 rounded-b-xl px-2 pb-2">
                    {phaseStages.map((stage) => (
                      <div
                        key={stage.id}
                        className="py-2.5 px-2 flex items-center justify-between border-b border-gray-100 last:border-b-0 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        <div className="flex-grow flex items-start gap-3">
                          <div className="flex items-center justify-center h-5 w-5 mt-0.5">
                            <Checkbox
                              id={`stage-${stage.id}`}
                              checked={stage.completed}
                              onCheckedChange={() => handleToggleStage(stage.id)}
                              disabled={isProjectCompleted || isSaving}
                              className="data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600 border-gray-300"
                            />
                          </div>
                          <div className="min-w-0">
                            <label
                              htmlFor={`stage-${stage.id}`}
                              className={`text-[13px] font-medium cursor-pointer block ${
                                stage.completed ? "line-through text-gray-400" : "text-gray-900"
                              }`}
                            >
                              {stage.name}
                            </label>

                            {stage.completedDate && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <CalendarIcon className="h-3 w-3 text-gray-400" />
                                <MonoValue
                                  value={format(new Date(stage.completedDate), "dd.MM.yyyy", { locale: tr })}
                                  tone="muted"
                                  size="meta"
                                />
                              </div>
                            )}

                            {stage.note && (
                              <p className="text-[11px] italic text-gray-500 mt-1">"{stage.note}"</p>
                            )}

                            {stage.photo && (
                              <div className="mt-2">
                                <div
                                  className="inline-block cursor-pointer"
                                  onClick={(e) => handleImageClick(e, stage)}
                                >
                                  {failedImages[stage.photo] ? (
                                    <div className="h-16 w-24 flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200">
                                      <ImageOff className="h-4 w-4 text-gray-400" />
                                    </div>
                                  ) : (
                                    <SecureImage
                                      src={stage.photo}
                                      alt={t('progress_tab.stage_photo_alt', { stageName: stage.name })}
                                      className="h-16 w-auto rounded-lg object-cover border border-gray-200 hover:opacity-90 transition-opacity"
                                      onError={() => handleImageError(stage.photo!)}
                                    />
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {!isProjectCompleted && (
                          <div className="flex gap-1 shrink-0 ml-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 border-gray-200 text-gray-500 hover:text-brand-600 hover:border-brand-200"
                              onClick={() => handleAddNote(stage.id)}
                              title={t('progress_tab.add_note')}
                              disabled={isSaving}
                            >
                              <PencilIcon className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-7 w-7 border-gray-200 text-gray-500 hover:text-brand-600 hover:border-brand-200"
                              onClick={() => handleAddPhoto(stage.id)}
                              title={t('progress_tab.add_photo')}
                              disabled={isSaving}
                            >
                              <ImageIcon className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              ))
            )}
          </div>
        </>
      )}

      {/* Note Dialog */}
      {selectedStage && (
        <NoteDialog
          isOpen={noteDialogOpen}
          onClose={() => setNoteDialogOpen(false)}
          onSave={handleSaveNote}
          initialNote={selectedStage.note || ''}
          stageName={selectedStage.name}
        />
      )}

      {/* Photo Upload Dialog */}
      {selectedStage && (
        <UniversalPhotoUploadDialog
          isOpen={photoDialogOpen}
          onClose={() => setPhotoDialogOpen(false)}
          onPhotoUploaded={handlePhotoUploaded}
          bucketName="site-photos"
          folderPath={`projects/${projectId}/stages`}
          title={t('progress_tab.add_stage_photo_title')}
          description={t('progress_tab.add_stage_photo_description', { stageName: selectedStage.name })}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {selectedStage && (
        <StageDeleteConfirmationDialog
          isOpen={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          title={t('progress_tab.delete_attachments_title')}
          description={t('progress_tab.delete_attachments_description', { stageName: selectedStage.name })}
          type={deleteType}
        />
      )}

      {/* Image Preview Dialog */}
      {selectedStage && previewImageUrl && (
        <ImagePreviewDialog
          isOpen={imagePreviewOpen}
          onClose={() => setImagePreviewOpen(false)}
          imageUrl={previewImageUrl}
          stageName={selectedStage.name}
        />
      )}
    </div>
  );
};

export default ProgressTab;
